import React, { useState } from 'react';
import { 
  Box, 
  IconButton, 
  TextField, 
  Stack,
  Typography,
  Chip,
  Card,
  Fade,
  Zoom,
  Tooltip,
  Paper,
  Avatar,
} from '@mui/material';
import { 
  Delete as DeleteIcon,
  Event as EventIcon,
  Assignment as TaskIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { formatDateToYYYYMMDD } from '../../../../Utils/constants';

const todayDate = new Date();

function PickerToLocal(dateObject) {
  const localDate = new Date(dateObject + "T00:00");
  return localDate;
}

// Helper function to get days until due date
function getDaysUntilDue(date) {
  const today = new Date();
  const dueDate = new Date(date);
  const diffTime = dueDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

// Helper function to get urgency color and styling
function getUrgencyConfig(daysUntil) {
  if (daysUntil < 0) return {
    color: 'error',
    bgColor: 'rgba(244, 67, 54, 0.1)',
    borderColor: 'rgba(244, 67, 54, 0.3)',
    gradient: 'linear-gradient(135deg, rgba(244, 67, 54, 0.1) 0%, rgba(244, 67, 54, 0.05) 100%)'
  };
  if (daysUntil <= 1) return {
    color: 'error',
    bgColor: 'rgba(244, 67, 54, 0.08)',
    borderColor: 'rgba(244, 67, 54, 0.2)',
    gradient: 'linear-gradient(135deg, rgba(244, 67, 54, 0.08) 0%, rgba(244, 67, 54, 0.04) 100%)'
  };
  if (daysUntil <= 3) return {
    color: 'warning',
    bgColor: 'rgba(255, 152, 0, 0.08)',
    borderColor: 'rgba(255, 152, 0, 0.2)',
    gradient: 'linear-gradient(135deg, rgba(255, 152, 0, 0.08) 0%, rgba(255, 152, 0, 0.04) 100%)'
  };
  if (daysUntil <= 7) return {
    color: 'info',
    bgColor: 'rgba(103, 126, 234, 0.08)',
    borderColor: 'rgba(103, 126, 234, 0.2)',
    gradient: 'linear-gradient(135deg, rgba(103, 126, 234, 0.08) 0%, rgba(103, 126, 234, 0.04) 100%)'
  };
  return {
    color: 'success',
    bgColor: 'rgba(76, 175, 80, 0.08)',
    borderColor: 'rgba(76, 175, 80, 0.2)',
    gradient: 'linear-gradient(135deg, rgba(76, 175, 80, 0.08) 0%, rgba(76, 175, 80, 0.04) 100%)'
  };
}

// Helper function to get urgency label
function getUrgencyLabel(daysUntil) {
  if (daysUntil < 0) return 'Overdue';
  if (daysUntil === 0) return 'Due Today';
  if (daysUntil === 1) return 'Due Tomorrow';
  if (daysUntil <= 7) return `${daysUntil} days left`;
  return `${daysUntil} days left`;
}

// Helper function to get urgency icon
function getUrgencyIcon(daysUntil) {
  if (daysUntil < 0) return WarningIcon;
  if (daysUntil <= 1) return AccessTimeIcon;
  if (daysUntil <= 7) return ScheduleIcon;
  return CheckCircleIcon;
}

export default function CampaignTimelineEntry({ event, onEditField, onDelete }) {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  
  const daysUntil = getDaysUntilDue(event.projected_date);
  const urgencyConfig = getUrgencyConfig(daysUntil);
  const urgencyLabel = getUrgencyLabel(daysUntil);
  const UrgencyIcon = getUrgencyIcon(daysUntil);
  
  // Check if objective is empty
  const isObjectiveEmpty = !event.objective || event.objective.trim() === '';

  return (
    <Zoom in timeout={300}>
      <Card 
        elevation={0}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          mb: 2,
          borderRadius: 4,
          border: '1px solid',
          borderColor: isHovered || isFocused ? urgencyConfig.borderColor : 'rgba(0,0,0,0.08)',
          background: isHovered || isFocused 
            ? urgencyConfig.gradient 
            : 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: isHovered ? 'translateY(-4px)' : 'translateY(0px)',
          boxShadow: isHovered 
            ? `0 12px 40px ${urgencyConfig.bgColor}` 
            : '0 4px 20px rgba(0,0,0,0.05)',
          position: 'relative',
          overflow: 'visible',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: `linear-gradient(90deg, ${urgencyConfig.borderColor} 0%, transparent 100%)`,
            borderRadius: '16px 16px 0 0',
            opacity: isHovered || isFocused ? 1 : 0.5,
            transition: 'opacity 0.3s ease'
          }
        }}
      >
        <Box sx={{ p: 3 }}>
          <Stack spacing={3}>
            {/* Header with urgency chip and actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{
                  width: 40,
                  height: 40,
                  background: urgencyConfig.gradient,
                  border: `1px solid ${urgencyConfig.borderColor}`
                }}>
                  <TaskIcon sx={{ color: `${urgencyConfig.color}.main`, fontSize: 20 }} />
                </Avatar>
                
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                    Milestone
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Campaign deliverable
                  </Typography>
                </Box>
              </Stack>
              
              <Stack direction="row" alignItems="center" spacing={1}>
                <Chip
                  icon={<UrgencyIcon sx={{ fontSize: 16 }} />}
                  label={urgencyLabel}
                  size="medium"
                  color={urgencyConfig.color}
                  variant={isHovered ? 'filled' : 'outlined'}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    borderRadius: 3,
                    transition: 'all 0.2s ease',
                    ...(isHovered && {
                      transform: 'scale(1.05)',
                      boxShadow: `0 4px 12px ${urgencyConfig.bgColor}`
                    })
                  }}
                />
                
                <Fade in={isHovered}>
                  <Tooltip title="Delete milestone" placement="top">
                    <IconButton
                      onClick={onDelete}
                      size="small"
                      sx={{
                        color: 'error.main',
                        backgroundColor: 'rgba(244, 67, 54, 0.1)',
                        border: '1px solid rgba(244, 67, 54, 0.2)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(244, 67, 54, 0.2)',
                          transform: 'scale(1.1) rotate(-5deg)',
                          boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)'
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Fade>
              </Stack>
            </Box>

            {/* Main content */}
            <Stack 
              direction={{ xs: 'column', md: 'row' }} 
              spacing={3} 
              alignItems="stretch"
            >
              <TextField
                label="Objective"
                value={event.objective}
                onChange={(e) => onEditField('objective', e.target.value)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                placeholder="What needs to be completed?"
                fullWidth
                multiline
                maxRows={3}
                error={isObjectiveEmpty}
                helperText={isObjectiveEmpty ? "Please enter an objective" : ""}
                InputProps={{
                  startAdornment: (
                    <TaskIcon sx={{ mr: 1.5, color: 'text.secondary', fontSize: 20 }} />
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    backgroundColor: 'white',
                    backdropFilter: 'blur(10px)',
                    fontSize: '1rem',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(103, 126, 234, 0.02)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'rgba(103, 126, 234, 0.05)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 8px 25px rgba(103, 126, 234, 0.15)',
                      '& fieldset': {
                        borderColor: '#667eea',
                        borderWidth: '2px'
                      }
                    }
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#667eea'
                  },
                  '& .MuiFormHelperText-root.Mui-error': {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }
                }}
              />
              
              <TextField
                label="Due Date"
                type="date"
                value={formatDateToYYYYMMDD(event.projected_date)}
                inputProps={{ 
                  min: formatDateToYYYYMMDD(todayDate)
                }}
                onChange={(e) => onEditField('projected_date', PickerToLocal(e.target.value))}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <EventIcon sx={{ mr: 1.5, color: 'text.secondary', fontSize: 20 }} />
                  )
                }}
                sx={{
                  width: { xs: '100%', md: 240 },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    backgroundColor: 'white',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(103, 126, 234, 0.02)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'rgba(103, 126, 234, 0.05)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 8px 25px rgba(103, 126, 234, 0.15)',
                      '& fieldset': {
                        borderColor: '#667eea',
                        borderWidth: '2px'
                      }
                    }
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#667eea'
                  }
                }}
              />
            </Stack>

            {/* Overdue warning */}
            {daysUntil < 0 && (
              <Fade in timeout={500}>
                <Paper sx={{ 
                  p: 2,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, rgba(244, 67, 54, 0.1) 0%, rgba(244, 67, 54, 0.05) 100%)',
                  border: '1px solid rgba(244, 67, 54, 0.2)',
                  backdropFilter: 'blur(10px)'
                }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar sx={{ 
                      width: 32, 
                      height: 32, 
                      backgroundColor: 'error.main',
                      animation: 'pulse 2s infinite'
                    }}>
                      <WarningIcon sx={{ fontSize: 18, color: 'white' }} />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" color="error.main" fontWeight={600}>
                        This milestone is overdue
                      </Typography>
                      <Typography variant="caption" color="error.main" sx={{ opacity: 0.8 }}>
                        Please select a future date to continue
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Fade>
            )}
          </Stack>
        </Box>

        {/* Keyframe for pulse animation */}
        <style>
          {`
            @keyframes pulse {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.05); }
            }
          `}
        </style>
      </Card>
    </Zoom>
  );
}